<script>
  import { onDestroy } from 'svelte';
  import { createEventDispatcher } from 'svelte';

  import tippy from 'tippy.js';

  import ImageUpload from '../utils/image-upload.js';
  import { handleHrefLink } from '../utils/link-helpers.js';
  import CreatorSpotlightModal from '@app-components/modal/CreatorSpotlightModal.svelte';
  import CookieHelper from '@utils/cookie-helper';

  const SLOTS = $$props.$$slots || {};
  const dispatch = createEventDispatcher();

  const DEFAULT_BORDER_STYLE = 'border: 2px solid rgb(236, 236, 236);';

  export let src;
  export let user = null;
  export let width = 32;
  export let height = width;
  export let href = null;
  export let tooltip = null;
  export let allowImageUpload = false;
  export let showBorder = false;
  export let plusIcon = true;
  export let showPlusIconWhenNoSrc = false;
  export let replaceOnUpload = false;
  export let cropper = null;
  export let notificationCount = null;
  export let borderStyle = DEFAULT_BORDER_STYLE;
  export let useGoto = false;
  export let showSpotlightRingOnly = false;
  export let showSpotlightIfApplicable = true;
  export let mobile = false;
  export let forceCursor = false;

  $: shouldAlwaysShowPlus = showPlusIconWhenNoSrc && (src == '' || src == null || src == undefined);

  $: showPlusIcon = shouldAlwaysShowPlus;

  $: getCreatorSpotlightAvatarClassnames =
    width > 90
      ? 'creator-spotlight creator-spotlight--large'
      : width >= 60 && width <= 90
        ? 'creator-spotlight creator-spotlight--medium'
        : width > 28 && width < 60
          ? 'creator-spotlight creator-spotlight--small'
          : 'creator-spotlight creator-spotlight--xsmall';

  $: getCreatorSpotlightRingSize =
    width > 90
      ? width - 14
      : width >= 60 && width <= 90
        ? width - 10
        : width > 28 && width < 60
          ? width - 6
          : width - 4;

  let uploading = false;
  let img;
  let fileInput;
  let uploadingPercentage = 0;
  let tippyInstance;
  let uppyInstance;
  let spotlightActive;
  let showSpotlightModal = false;

  let handleClick = function (e) {
    const spotlightVisited = spotlightActive ? CookieHelper.hasVisitedProfileAndSpotlight(user) : false;
    if (allowImageUpload) {
      fileInput.click();
    } else if (href && (!spotlightActive || spotlightVisited)) {
      handleHrefLink(e, href, { useGoto: useGoto });
    } else if (spotlightActive && !showSpotlightRingOnly && (!spotlightVisited || (user && location.pathname == `/${user.username}`))) {
      CookieHelper.setSpotlightVisitsCookie(user);
      e.stopPropagation();
      showSpotlightModal = true;
    } else {
      dispatch('click');
    }
  };

  let attachImageUpload = async function () {
    uppyInstance = await ImageUpload.attach(
      fileInput,
      function (data) {
        uploading = false;

        dispatch('upload-complete', {
          images: data,
        });

        if (replaceOnUpload) {
          if (data && data.length > 0 && data[0] && data[0].uploadURL) {
            src = data[0].uploadURL;
          }
        }
      },
      (percentage, file, progress) => {
        uploading = true;
        uploadingPercentage = percentage;
        dispatch('upload-progress', {
          percentage: percentage,
          file: file,
          progress: progress,
        });
      },
      {
        maxNumberOfFiles: 1,
        cropper: cropper,
      }
    );
  };

  $: if (src && src.match(/ui-avatar/)) {
    showBorder = true;
    borderStyle = DEFAULT_BORDER_STYLE;
  }

  $: spotlightActive = showSpotlightIfApplicable ? Boolean(user && user.spotlight_active) : false;

  $: showBorder = showBorder || (src && src.match(/ui-avatar/));

  $: if (fileInput && allowImageUpload) {
    attachImageUpload();
  }

  $: if (img && tooltip) {
    tippyInstance = tippy(img, { content: tooltip });
  }

  onDestroy(() => {
    if (tippyInstance) {
      tippyInstance.destroy();
    }

    if (uppyInstance) {
      uppyInstance.destroy();
    }
  });
</script>

<style style="text/scss" lang="scss">
  .avatar {
    cursor: pointer;
    display: inline-block;
    object-fit: cover;
    border-radius: 9999px;
  }

  .avatar-plus-icon {
    border-radius: 9999px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgb(168, 147, 249);
    color: white;
  }

  .current-user-notification-count {
    position: absolute;
    top: -7px;
    right: -5px;
    background-color: #ff5567;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    font-size: 11px;
  }

  .creator-spotlight {
    border-radius: 9999px;
    background: linear-gradient(0deg, #6bbed1 10%, #ff6b6b 52%, #fff08e 98.5%);
    background-size: 100% 100%;
    padding: 5px;

    &--small {
      padding: 3px;

      ~ .ring {
        top: 3px;
        left: 3px;
      }
    }

    &--xsmall {
      padding: 2px;

      ~ .ring {
        top: 2px;
        left: 2px;
      }
    }

    &--large {
      padding: 7px;

      ~ .ring {
        top: 7px;
        left: 7px;
        border-width: 2px;
      }
    }
  }

  .ring {
    position: absolute;
    top: 5px;
    left: 5px;
    border: 1px solid white;
    border-radius: 50%;
  }
</style>

{#if showSpotlightModal}
  <CreatorSpotlightModal
    mobile={mobile}
    user={user}
    bind:showModal={showSpotlightModal}
    on:close={() => {
      showSpotlightModal = false;
    }} />
{/if}

{#if allowImageUpload}
  <div bind:this={fileInput} style="display: none" />
{/if}

{#if allowImageUpload && (showPlusIcon || uploading)}
  <div style="display: inline-block;" class={$$props.uploadingClass}>
    {#if SLOTS.empty}
      <div
        on:mouseout={() => {
          if (!shouldAlwaysShowPlus) {
            showPlusIcon = false;
          }
        }}
        on:blur={() => {
          if (!shouldAlwaysShowPlus) {
            showPlusIcon = false;
          }
        }}
        on:click|preventDefault={handleClick}
        style="width: {width}px; height: {height}px; font-size: {width / 2}px;
        padding-bottom: {width / 12}px; position: absolute; cursor: pointer;" />
      <slot name="empty" />
    {:else}
      <div
        on:mouseout={() => {
          showPlusIcon = false;
        }}
        on:blur={() => {
          showPlusIcon = false;
        }}
        on:click|preventDefault={handleClick}
        class="avatar-plus-icon {$$props.plusIconClass}"
        style="width: {width}px; height: {height}px; font-size: {width / 2}px;
                    padding-bottom: {width / 12}px;">
        {#if uploading}
          <span style="font-size: {width / 4}px; margin-top: {width / 8}px;">
            {uploadingPercentage}%
          </span>
        {:else}+{/if}
      </div>
    {/if}
  </div>
{:else}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <span
    style="position: relative; display: inline-block;"
    on:mouseleave={() => {
      showPlusIcon = false;
    }}
    on:mouseenter={() => {
      if (plusIcon) {
        showPlusIcon = true;
      }
    }}
    on:click|preventDefault={handleClick}>
    <img
      bind:this={img}
      class="avatar {spotlightActive || showSpotlightRingOnly
        ? getCreatorSpotlightAvatarClassnames
        : ''} {$$props.class}"
      style="width: {width}px; height: {height}px; {showBorder ? borderStyle : ''}"
      alt={user ? `${user.name} profile picture` : ''}
      src={src} />

    {#if spotlightActive || showSpotlightRingOnly}
      <div
        class="ring"
        style="width: {getCreatorSpotlightRingSize}px; height: {getCreatorSpotlightRingSize}px; cursor: {showSpotlightRingOnly && !forceCursor
          ? 'default'
          : 'pointer'}">
      </div>
    {/if}
    {#if notificationCount && notificationCount > 0}
      <div class="current-user-notification-count">
        {notificationCount}
      </div>
    {/if}
  </span>

  <slot name="additional-content" />
{/if}
